import React, { useState, useMemo, useEffect } from 'react';
import { Briefcase, MapPin, User } from 'lucide-react';
import { useTasks } from '../context/TasksContext';
import { useLeads } from '../context/LeadsContext';
import { useCompany } from '../context/CompanyContext';
import { db } from '../firebase';
import { collection, getDocs, getDoc, doc } from 'firebase/firestore';
import WorkOrderSheet from '../features/clients/components/profile/WorkOrderSheet';
import { useJobInvoicesMap } from '../features/jobs/useJobInvoicesMap';
import { deriveJobFinancialState, jobFinancialBadgeClass } from '../lib/invoiceFinancials';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  classifyJob, passesDateFilter, bucketsForFilter, resolveJobCustomer, JOB_BUCKETS,
} from '../lib/jobClassification';

// ─── Constants ────────────────────────────────────────────────────────────────

// Top-level filters. Counts + grouping all flow through the shared classifier.
const FILTER_OPTIONS = [
  { value: 'active',      label: 'Active'      },
  { value: 'all',         label: 'All Jobs'    },
  { value: 'unscheduled', label: 'Unscheduled' },
  { value: 'scheduled',   label: 'Scheduled'   },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed',   label: 'Completed'   },
  { value: 'cancelled',   label: 'Cancelled'   },
];

// Section headers per lifecycle bucket, in display order.
const GROUP_ORDER = [
  { key: JOB_BUCKETS.UNSCHEDULED, label: 'Unscheduled' },
  { key: JOB_BUCKETS.SCHEDULED,   label: 'Scheduled'   },
  { key: JOB_BUCKETS.IN_PROGRESS, label: 'In Progress' },
  { key: JOB_BUCKETS.COMPLETED,   label: 'Completed'   },
  { key: JOB_BUCKETS.CANCELLED,   label: 'Cancelled'   },
  { key: JOB_BUCKETS.UNKNOWN,     label: 'Unknown Status' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getAddress = (lead) => {
  if (!lead) return null;
  if (lead.addressDisplay) return lead.addressDisplay;
  if (!lead.address) return null;
  if (typeof lead.address === 'string') return lead.address;
  const { line1, city, state, zip, street, streetNumber, streetName } = lead.address;
  const streetPart = line1 || street || `${streetNumber || ''} ${streetName || ''}`.trim();
  return [streetPart, city, state, zip].filter(Boolean).join(', ') || null;
};

const formatTimeSlot = (startIso, endIso) => {
  if (!startIso) return null;
  try {
    const start = new Date(startIso);
    if (isNaN(start.getTime())) return null;
    const datePart = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    if (!endIso) return `${datePart} · ${startTime}`;
    const end = new Date(endIso);
    if (isNaN(end.getTime())) return `${datePart} · ${startTime}`;
    const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} · ${startTime} – ${endTime}`;
  } catch {
    return null;
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const Jobs = () => {
  const { tasks } = useTasks();
  const { leads } = useLeads();
  const { companyId } = useCompany();

  const [selectedTask, setSelectedTask]   = useState(null);
  const [workOrderOpen, setWorkOrderOpen] = useState(false);
  const [statusFilter, setStatusFilter]   = useState('active'); // default: scheduled + in_progress
  const [dateFilter, setDateFilter]       = useState('all');
  const [techFilter, setTechFilter]       = useState('all');
  const [companyUsers, setCompanyUsers]   = useState([]);

  // Load company members for the technician filter
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'companies', companyId, 'users'));
        const members = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const joined = await Promise.all(
          members.map(async (m) => {
            try {
              const profileSnap = await getDoc(doc(db, 'users', m.id));
              return { id: m.id, ...(profileSnap.exists() ? profileSnap.data() : {}) };
            } catch {
              return { id: m.id };
            }
          })
        );
        setCompanyUsers(joined);
      } catch (err) {
        console.error('[Jobs] Failed to load company users:', err);
      }
    })();
  }, [companyId]);

  // Lead and user lookup maps
  const leadMap = useMemo(() => {
    const map = {};
    leads.forEach(l => { map[l.id] = l; });
    return map;
  }, [leads]);

  const userMap = useMemo(() => {
    const map = {};
    companyUsers.forEach(u => {
      map[u.id] = u.displayName || u.name || u.email || null;
    });
    return map;
  }, [companyUsers]);

  // Derive live task from TasksContext so WorkOrderSheet reflects real-time updates
  // (e.g. status changes from Start Job / Mark Complete without needing to reopen)
  const liveSelectedTask = useMemo(
    () => selectedTask ? (tasks.find(t => t.id === selectedTask.id) ?? selectedTask) : null,
    [tasks, selectedTask]
  );

  // The lead associated with the currently-open work order
  const selectedLead = useMemo(
    () => leads.find(l => l.id === selectedTask?.leadId) || null,
    [leads, selectedTask]
  );

  // Group jobs into lifecycle buckets via the shared classifier, applying
  // tech + date filters. Date filter never hides field-active work (handled
  // inside passesDateFilter). Counts and rendering use this same dataset.
  const groupedJobs = useMemo(() => {
    const jobs = tasks.filter(t => {
      if (t.type !== 'job') return false;
      if (techFilter !== 'all' && t.assignedTo !== techFilter) return false;
      return passesDateFilter(t, dateFilter);
    });

    const buckets = {
      [JOB_BUCKETS.UNSCHEDULED]: [],
      [JOB_BUCKETS.SCHEDULED]:   [],
      [JOB_BUCKETS.IN_PROGRESS]: [],
      [JOB_BUCKETS.COMPLETED]:   [],
      [JOB_BUCKETS.CANCELLED]:   [],
      [JOB_BUCKETS.UNKNOWN]:     [],
    };
    jobs.forEach(t => {
      const { bucket } = classifyJob(t);
      (buckets[bucket] || buckets[JOB_BUCKETS.UNKNOWN]).push(t);
    });
    Object.values(buckets).forEach(list =>
      list.sort((a, b) => new Date(a.scheduledStart || 0) - new Date(b.scheduledStart || 0))
    );
    return buckets;
  }, [tasks, techFilter, dateFilter]);

  // Count for any top-level filter = sum of its mapped buckets (same dataset).
  const filterCount = useMemo(() => (filterValue) =>
    bucketsForFilter(filterValue).reduce((sum, b) => sum + (groupedJobs[b]?.length || 0), 0),
  [groupedJobs]);

  // Which bucket keys to render for the active status filter.
  const visibleGroupKeys = useMemo(() =>
    bucketsForFilter(statusFilter).filter(k => (groupedJobs[k]?.length || 0) > 0),
  [statusFilter, groupedJobs]);

  const totalVisible = bucketsForFilter(statusFilter).reduce(
    (sum, k) => sum + (groupedJobs[k]?.length || 0), 0
  );

  const openWorkOrder = (task) => { setSelectedTask(task); setWorkOrderOpen(true); };
  const closeWorkOrder = () => { setWorkOrderOpen(false); setSelectedTask(null); };

  // Phase 2B — derive financial state for completed / cancelled jobs only, from
  // authoritative invoice records (nothing is written to the job). Bounded,
  // tenant-scoped, real-time; the hook chunks taskIds (no per-row listeners).
  const financialTaskIds = useMemo(() => ([
    ...(groupedJobs[JOB_BUCKETS.COMPLETED] || []),
    ...(groupedJobs[JOB_BUCKETS.CANCELLED] || []),
  ].map(t => t.id)), [groupedJobs]);
  const jobInvoicesMap = useJobInvoicesMap(companyId, financialTaskIds);

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-5" data-testid="jobs-page">

      {/* Header */}
      <div className="flex items-center gap-3">
        <Briefcase className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-secondary">Jobs</h1>
          <p className="text-sm text-muted-foreground">
            {totalVisible} {statusFilter === 'active' ? 'active ' : ''}job{totalVisible !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="space-y-2">
        {/* Status pills — WRAP on all sizes so every filter is discoverable
            (previously a mobile horizontal scroller clipped In Progress/
            Completed/Cancelled off-screen). */}
        <div className="flex flex-wrap gap-2">
          {FILTER_OPTIONS.map(opt => {
            const count = filterCount(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                data-testid={`jobs-filter-${opt.value}`}
                className={`px-4 py-2 lg:px-3 lg:py-1 rounded-full text-xs font-medium border transition-colors min-h-[44px] lg:min-h-0 ${
                  statusFilter === opt.value
                    ? 'bg-[var(--cb-orange)] text-white border-[var(--cb-orange)]'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                }`}
              >
                {opt.label}
                {count > 0 && (
                  <span className="ml-1.5 opacity-75" data-testid={`jobs-filter-count-${opt.value}`}>({count})</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Date + Tech selects — right-aligned row */}
        <div className="flex items-center gap-2 justify-end">
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="h-11 lg:h-8 w-36 lg:w-32 text-xs" data-testid="jobs-date-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dates</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
            </SelectContent>
          </Select>

          {companyUsers.length > 0 && (
            <Select value={techFilter} onValueChange={setTechFilter}>
              <SelectTrigger className="h-11 lg:h-8 w-44 lg:w-40 text-xs" data-testid="jobs-tech-filter">
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                {companyUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.displayName || u.name || u.email || u.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Job groups */}
      {visibleGroupKeys.map(key => {
        const jobs = groupedJobs[key];
        if (!jobs || jobs.length === 0) return null;
        const groupLabel = GROUP_ORDER.find(g => g.key === key)?.label ?? key;

        return (
          <section key={key} data-testid={`jobs-group-${key}`}>
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{groupLabel}</h2>
              <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-medium">
                {jobs.length}
              </span>
            </div>

            <div className="space-y-2">
              {jobs.map(task => {
                const lead        = leadMap[task.leadId];
                const cust        = resolveJobCustomer(task, lead);
                const customer    = cust.name;
                const rawService  = task.title || lead?.service || 'Job';
                // Never repeat the headline as the service line (happens when the
                // customer fell back to the job title).
                const serviceLine = rawService && rawService !== customer
                  ? rawService
                  : (lead?.service && lead.service !== customer ? lead.service : null);
                const address     = getAddress(lead);
                const timeLabel   = formatTimeSlot(task.scheduledStart, task.scheduledEnd);
                const meta        = classifyJob(task);
                const techName    = task.assignedTo ? (userMap[task.assignedTo] ?? null) : null;

                // Phase 2B — second-dimension financial state (completed/cancelled only)
                const isCompletedBucket = key === JOB_BUCKETS.COMPLETED;
                const isCancelledBucket = key === JOB_BUCKETS.CANCELLED;
                const fin = (isCompletedBucket || isCancelledBucket)
                  ? deriveJobFinancialState({ task, invoices: jobInvoicesMap[task.id] || [] })
                  : null;
                // Completed rows always show a financial line (incl. Unbilled).
                // Cancelled rows only when a real financial record makes it meaningful.
                const showFinancial = !!fin && (
                  isCompletedBucket || (isCancelledBucket && fin.state !== 'unbilled' && fin.state !== 'covered')
                );
                const finBadge = showFinancial ? (
                  <span
                    data-testid={`job-financial-${task.id}`}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${jobFinancialBadgeClass(fin.state)}`}
                  >
                    {fin.label}
                  </span>
                ) : null;

                return (
                  <button
                    key={task.id}
                    type="button"
                    onClick={() => openWorkOrder(task)}
                    data-testid={`job-card-${task.id}`}
                    className="w-full text-left bg-white border border-gray-200 rounded-xl lg:rounded-lg px-4 py-4 lg:py-3 hover:border-[var(--cb-orange)] hover:shadow-sm active:scale-[0.98] transition-all min-h-[80px] lg:min-h-0"
                    style={{ borderLeft: `3px solid ${meta.color}` }}
                  >
                    {/* ── Mobile layout: client-first, stacked ─────────────────── */}
                    <div className="lg:hidden">
                      {/* Row 1: client name + status badge */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-base text-gray-900 truncate leading-snug">{customer}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${meta.badgeCls}`}>
                          {meta.badgeLabel}
                        </span>
                      </div>
                      {/* Row 2: service type */}
                      {serviceLine && (
                        <div className="text-sm text-gray-500 mt-0.5">{serviceLine}</div>
                      )}
                      {/* Row 2.5: financial state (completed/cancelled only) */}
                      {finBadge && (
                        <div className="mt-1.5">{finBadge}</div>
                      )}
                      {/* Row 3: address */}
                      {address && (
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-gray-400">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{address}</span>
                        </div>
                      )}
                      {/* Row 4: date/time */}
                      {timeLabel && (
                        <div className="text-xs text-gray-500 mt-1.5 font-medium">{timeLabel}</div>
                      )}
                      {/* Row 5: assigned tech (de-emphasised) */}
                      {techName && (
                        <div className="flex items-center gap-1 mt-1 text-[11px] text-gray-400">
                          <User className="h-3 w-3" />
                          <span>{techName}</span>
                        </div>
                      )}
                    </div>

                    {/* ── Desktop layout: service-first, side-by-side ──────────── */}
                    <div className="hidden lg:flex items-start justify-between gap-3">
                      {/* Left: service (or name), customer, address */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-gray-900">{serviceLine || customer}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${meta.badgeCls}`}>
                            {meta.badgeLabel}
                          </span>
                        </div>
                        {serviceLine && (
                          <div className="mt-0.5 text-xs text-gray-600">{customer}</div>
                        )}
                        {address && (
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{address}</span>
                          </div>
                        )}
                      </div>
                      {/* Right: time + tech */}
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {finBadge}
                        {timeLabel && (
                          <div className="text-xs text-gray-500 text-right">{timeLabel}</div>
                        )}
                        {techName && (
                          <div className="flex items-center gap-1 text-[11px] text-gray-400">
                            <User className="h-3 w-3" />
                            <span>{techName}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* Empty state */}
      {totalVisible === 0 && (
        <div className="text-center py-16 text-gray-400" data-testid="jobs-empty-state">
          <Briefcase className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No jobs match the current filters.</p>
          {statusFilter !== 'all' && (
            <button
              type="button"
              className="mt-2 text-xs text-[var(--cb-orange)] hover:underline"
              onClick={() => setStatusFilter('all')}
            >
              Show all jobs
            </button>
          )}
        </div>
      )}

      {/* WorkOrderSheet — opens directly as overlay without routing to client profile */}
      <WorkOrderSheet
        task={liveSelectedTask}
        client={selectedLead}
        open={workOrderOpen}
        onClose={closeWorkOrder}
        onCompleted={closeWorkOrder}
      />
    </div>
  );
};

export default Jobs;
